import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Menu,
  MenuItem,
  Collapse,
  Avatar,
  Divider,
  Tooltip,
  Button,
  ButtonBase,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Assignment as AssignmentIcon,
  ExpandLess,
  ExpandMore,
  Cookie as CookieIcon,
  Business as BusinessIcon,
  MenuBook as MenuBookIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Help as HelpIcon,
  History as HistoryIcon,
  Warning as WarningIcon,
  Send as SendIcon,
  Gavel as GavelIcon,
  AdminPanelSettings as CisoIcon,
  AccountTree as OpenMetadataIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Logout as LogoutIcon,
  SwitchAccount as SwitchAccountIcon,
  PersonOutline as ProfileIcon,
  ManageAccountsOutlined as AccountSettingsIcon,
  ReceiptLong as ReceiptLongIcon,
  SpaceDashboard as AdminPanelIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import OrgSwitcher from './OrgSwitcher';
import AnnouncementBanner from './AnnouncementBanner';
import { useActiveOrg } from '../contexts/OrgContext';
import { BRANDING } from '../branding';
import { API_BASE_URL } from '../utils';

const drawerWidth = 280;

// Shell design tokens — clean white surfaces, slate text, single orange accent.
const SHELL = {
  accent: '#FF6600',
  accentDk: '#C2410C',
  accentSoft: 'rgba(255,102,0,0.10)',
  text: '#0F172A', // slate-900
  textDim: '#64748B', // slate-500
  navText: '#334155', // slate-700
  border: '#E6EAF0',
  surface: '#FFFFFF',
  hover: '#F1F5F9', // slate-100
  danger: '#DC2626',
};
const FONT = '"Inter", "Roboto", "Helvetica", "Arial", sans-serif';

const menuItems = [
  {
    title: 'Dashboard',
    icon: <DashboardIcon />,
    path: '/dashboard',
    // Consent-metrics dashboard is account/org data — hidden for the platform-only
    // super admin, whose dashboard is the Admin Panel. Also requires dashboard:view
    // so a restricted RBAC role without it doesn't see an inaccessible dashboard.
    hideForSuperAdmin: true,
    requiredPerm: 'dashboard:view',
  },
  {
    title: 'Admin Panel',
    icon: <AdminPanelIcon />,
    path: '/admin-panel',
    platformPerm: 'platform_audit:view', // every platform role holds this
  },
  {
    title: 'Consent Management',
    icon: <AssignmentIcon />,
    path: '/consent-management',
    subItems: [
      { title: 'Purpose Management', icon: <AssignmentIcon />, path: '/consent-management/purposes', requiredPerm: 'purposes:view' },
      { title: 'Notice Builder', icon: <AssignmentIcon />, path: '/consent-management/notices', requiredPerm: 'notices:view' },
      { title: 'Consent Logs', icon: <HistoryIcon />, path: '/consent-management/logs', requiredPerm: 'consents:view' },
      { title: 'Consent Expiry', icon: <WarningIcon />, path: '/consent-management/expiry', requiredPerm: 'purposes:view' },
      { title: 'Communications', icon: <SendIcon />, path: '/communications', requiredPerm: 'communications:manage', launch: 'commGateway' },
    ],
  },
  {
    // DPDP Act 2023 data principal rights & grievance redressal console.
    title: 'Data Principal Rights',
    icon: <GavelIcon />,
    path: '/data-principal-rights',
    requiredPerm: 'dpr:view',
  },
  {
    title: 'Cookie Management',
    icon: <CookieIcon />,
    path: '/cookie-management',
    subItems: [
      { title: 'Cookie Consent Builder', icon: <CookieIcon />, path: '/cookie-management/cookie-consent', requiredPerm: 'cookies:view' },
    ],
  },
  {
    title: 'CISO Assistant',
    icon: <CisoIcon />,
    requiredIntegration: 'ciso',
    // Org-scoped tenant tool — not a platform-admin concern, so hide it in the
    // Admin Panel / for platform admins (still shows in the business workspace).
    hideForSuperAdmin: true,
    subItems: [
      {
        title: 'Overview',
        subItems: [
          { title: 'Analytics', path: '/ciso-assistant/analytics' },
          { title: 'Reports', path: '/ciso-assistant/reports' },
          { title: 'My Assignments', path: '/ciso-assistant/my-assignments' },
          { title: 'Audit Dashboard', path: '/ciso-assistant/auditee-dashboard' },
          { title: 'Presets', path: '/ciso-assistant/presets' },
        ],
      },
      {
        title: 'Organization',
        subItems: [
          { title: 'Domains', path: '/ciso-assistant/folders' },
          { title: 'Perimeters', path: '/ciso-assistant/perimeters' },
          { title: 'Users', path: '/ciso-assistant/users' },
          { title: 'Teams', path: '/ciso-assistant/teams' },
          { title: 'User Groups', path: '/ciso-assistant/user-groups' },
          { title: 'Role Assignments', path: '/ciso-assistant/role-assignments' },
        ],
      },
      {
        title: 'Catalog',
        subItems: [
          { title: 'Frameworks', path: '/ciso-assistant/frameworks' },
          { title: 'Threats', path: '/ciso-assistant/threats' },
          { title: 'Reference Controls', path: '/ciso-assistant/reference-controls' },
          { title: 'Requirement Mapping Sets', path: '/ciso-assistant/requirement-mapping-sets' },
          { title: 'Risk Matrices', path: '/ciso-assistant/risk-matrices' },
        ],
      },
      {
        title: 'Assets Management',
        subItems: [
          { title: 'Assets', path: '/ciso-assistant/assets' },
          { title: 'Business Impact Analysis', path: '/ciso-assistant/business-impact-analysis' },
        ],
      },
      {
        title: 'Operations',
        subItems: [
          { title: 'Applied Controls', path: '/ciso-assistant/applied-controls' },
          { title: 'Calendar', path: '/ciso-assistant/calendar' },
          { title: 'X-Rays', path: '/ciso-assistant/x-rays' },
          { title: 'Incidents', path: '/ciso-assistant/incidents' },
          { title: 'Tasks', path: '/ciso-assistant/task-templates' },
        ],
      },
      {
        title: 'Governance',
        subItems: [
          { title: 'Libraries', path: '/ciso-assistant/libraries' },
          { title: 'Policies', path: '/ciso-assistant/policies' },
          { title: 'Organisation Issues', path: '/ciso-assistant/organisation-issues' },
          { title: 'Organisation Objectives', path: '/ciso-assistant/organisation-objectives' },
          { title: 'Risk Acceptances', path: '/ciso-assistant/risk-acceptances' },
          { title: 'Validation Flows', path: '/ciso-assistant/validation-flows' },
          { title: 'Security Exceptions', path: '/ciso-assistant/security-exceptions' },
          { title: 'Follow Up', path: '/ciso-assistant/findings-assessments' },
        ],
      },
      {
        title: 'Risk',
        subItems: [
          { title: 'Risk Assessments', path: '/ciso-assistant/risk-assessments' },
          { title: 'EBIOS RM', path: '/ciso-assistant/ebios-rm' },
          { title: 'Quantitative Risk Studies', path: '/ciso-assistant/quantitative-risk-studies' },
          { title: 'Risk Scenarios', path: '/ciso-assistant/risk-scenarios' },
          { title: 'Scoring Assistant', path: '/ciso-assistant/scoring-assistant' },
          { title: 'Vulnerabilities', path: '/ciso-assistant/vulnerabilities' },
        ],
      },
      {
        title: 'Compliance',
        subItems: [
          { title: 'Compliance Assessments', path: '/ciso-assistant/compliance-assessments' },
          { title: 'Evidences', path: '/ciso-assistant/evidences' },
          { title: 'Recap', path: '/ciso-assistant/recap' },
        ],
      },
      {
        title: 'Metrology',
        subItems: [
          { title: 'Metric Definitions', path: '/ciso-assistant/metric-definitions' },
          { title: 'Metric Instances', path: '/ciso-assistant/metric-instances' },
          { title: 'Dashboards', path: '/ciso-assistant/dashboards' },
        ],
      },
      {
        title: 'Third Party',
        subItems: [
          { title: 'TPRM Overview', path: '/ciso-assistant/analytics/tprm' },
          { title: 'Entities', path: '/ciso-assistant/entities' },
          { title: 'Representatives', path: '/ciso-assistant/representatives' },
          { title: 'Solutions', path: '/ciso-assistant/solutions' },
          { title: 'Contracts', path: '/ciso-assistant/contracts' },
          { title: 'Entity Assessments', path: '/ciso-assistant/entity-assessments' },
        ],
      },
      {
        title: 'Privacy',
        subItems: [
          { title: 'Overview', path: '/ciso-assistant/analytics/gdpr' },
          { title: 'Processings Register', path: '/ciso-assistant/processings' },
          { title: 'Personal Data', path: '/ciso-assistant/personal-data' },
          { title: 'Purposes', path: '/ciso-assistant/purposes' },
          { title: 'Right Requests', path: '/ciso-assistant/right-requests' },
          { title: 'Data Breaches', path: '/ciso-assistant/data-breaches' },
        ],
      },
      {
        title: 'Project Management',
        subItems: [
          { title: 'Generic Collections', path: '/ciso-assistant/generic-collections' },
          { title: 'Accreditations', path: '/ciso-assistant/accreditations' },
        ],
      },
      {
        title: 'Extra',
        subItems: [
          { title: 'Labels', path: '/ciso-assistant/filtering-labels' },
          { title: 'Terminologies', path: '/ciso-assistant/terminologies' },
          { title: 'Settings', path: '/ciso-assistant/settings' },
          { title: 'Backup & Restore', path: '/ciso-assistant/backup-restore' },
          { title: 'Experimental', path: '/ciso-assistant/experimental' },
        ],
      },
    ],
  },
  {
    title: 'Open Metadata',
    icon: <OpenMetadataIcon />,
    requiredIntegration: 'om',
    // Org-scoped tenant tool — hidden for platform admins / in the Admin Panel.
    hideForSuperAdmin: true,
    subItems: [
      { title: 'Home', path: '/open-metadata/my-data' },
      { title: 'Explore', path: '/open-metadata/explore' },
      { title: 'Lineage', path: '/open-metadata/lineage' },
      {
        title: 'Observability',
        subItems: [
          { title: 'Data Quality', path: '/open-metadata/data-quality' },
          { title: 'Incident Manager', path: '/open-metadata/incident-manager' },
          { title: 'Alerts', path: '/open-metadata/observability/alerts' },
          { title: 'Rules Library', path: '/open-metadata/rules-library' },
        ],
      },
      { title: 'Insights', path: '/open-metadata/data-insights/data-assets' },
      {
        title: 'Domains',
        subItems: [
          { title: 'Domains', path: '/open-metadata/domain' },
          { title: 'Data Products', path: '/open-metadata/dataProduct' },
        ],
      },
      {
        title: 'Govern',
        subItems: [
          { title: 'Glossary', path: '/open-metadata/glossary' },
          { title: 'Classification', path: '/open-metadata/tags' },
          { title: 'Metrics', path: '/open-metadata/metrics' },
          { title: 'Column Bulk Operations', path: '/open-metadata/column-bulk-operations' },
        ],
      },
      { title: 'Settings', path: '/open-metadata/settings' },
    ],
  },
  {
    title: 'Billing',
    icon: <ReceiptLongIcon />,
    subItems: [
      { title: 'My Billing', path: '/billing', requiredPerm: 'billing:view' },
      { title: 'Billing Admin', path: '/billing-admin', platformPerm: 'platform_billing:view' },
      { title: 'Usage Monitor', path: '/usage-monitor', platformPerm: 'platform_billing:view' },
    ],
  },
  {
    title: 'Organization',
    icon: <BusinessIcon />,
    subItems: [
      { title: 'Business Accounts', path: '/accounts', platformPerm: 'platform_tenants:view' },
      { title: 'Tenant Hub', path: '/organizations', platformPerm: 'platform_tenants:view' },
      { title: 'Platform Admins', path: '/platform-admins', superAdminOnly: true },
      { title: 'Roles', path: '/organization/roles', platformPerm: 'platform_tenants:manage' },
      { title: 'Admin Activity', path: '/admin/activity', platformPerm: 'platform_audit:view' },
      { title: 'My Business', path: '/my-business', requiredPerm: 'account:view' },
      { title: 'Add Organization', path: '/organizations/new', requiredPerm: 'organization:create' },
      { title: 'User Management', path: '/organization/users', requiredPerm: 'users:view' },
      { title: 'Role Management', path: '/organization/roles', requiredPerm: 'roles:view' },
      { title: 'Audit Logs', path: '/organization/audit-logs', requiredPerm: 'audit_logs:view' },
    ],
  },
  {
    title: 'Content',
    icon: <QuestionAnswerIcon />,
    subItems: [
      { title: 'FAQ Manager', path: '/faq-manager', platformPerm: 'platform_content:manage' },
      { title: 'Announcements', path: '/content/announcements', platformPerm: 'platform_content:manage' },
      { title: 'Compliance Checklist', path: '/content/checklist', platformPerm: 'platform_content:manage' },
      { title: 'Testimonials', path: '/content/testimonials', platformPerm: 'platform_content:manage' },
      { title: 'Legal Pages', path: '/content/legal', platformPerm: 'platform_content:manage' },
    ],
  },
  {
    title: 'Documentation',
    icon: <MenuBookIcon />,
    path: '/documentation',
  },
  {
    title: 'Support',
    icon: <HelpIcon />,
    path: '/support',
  },
  {
    title: 'Support Inbox',
    icon: <HelpIcon />,
    path: '/support-inbox',
    platformPerm: 'platform_support:view',
  },
];

function Layout({ children, pageTitle }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openSubMenu, setOpenSubMenu] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Section (parent nav group) for the current route — shown as lightweight
  // breadcrumb context in the top bar, instead of duplicating the page title
  // that each page already renders (with its tagline) in the main container.
  const currentSection = (() => {
    const path = location.pathname;
    for (const item of menuItems) {
      if (!item.subItems) continue;
      const inGroup = item.subItems.some((si) =>
        (si.path && path.startsWith(si.path)) ||
        (si.subItems && si.subItems.some((x) => x.path && path.startsWith(x.path)))
      );
      if (inGroup) return item.title;
    }
    return null;
  })();
  const { user, logout, hasPermission, isSuperAdmin, hasIntegration, hasPlatformPermission, isPlatformAdmin, isDualAccess, roles, selectRole } = useAuth();

  // Which of the two panels a dual-access user is in (keeps them separate).
  const panelMode = isDualAccess ? ((() => { try { return sessionStorage.getItem('panelMode'); } catch (e) { return null; } })()) : null;
  // Classify a nav entry: platform-only, business/operational, or neutral chrome.
  const panelOf = (item) => {
    if (item.platformPerm || item.superAdminOnly) return 'platform';
    if (item.requiredPerm || item.hideForSuperAdmin) return 'business';
    return 'both';
  };

  const handleSwitchRole = async () => {
    handleProfileMenuClose();
    try { await selectRole(null); } catch (e) { /* ignore */ }
    navigate('/');
  };

  const handleSwitchPanel = () => {
    handleProfileMenuClose();
    try { sessionStorage.removeItem('panelMode'); } catch (e) { /* ignore */ }
    navigate('/');
  };
  const { isImpersonating, exitImpersonation } = useActiveOrg();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [userInfo, setUserInfo] = useState(null);
  const [userLoading, setUserLoading] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Open the Communication Gateway in a NEW TAB without flashing the CMS shell:
  // open a blank tab in the click (so it isn't popup-blocked), fetch the SSO
  // token here (this tab is already authenticated), then point the tab straight
  // at the gateway. On failure the message is shown in the blank tab.
  const openCommGateway = async () => {
    const w = window.open('', '_blank');
    if (w) {
      try {
        w.document.write('<!doctype html><meta charset="utf-8"><title>Communications</title>'
          + '<body style="margin:0;font:15px system-ui,-apple-system,sans-serif;color:#64748B;'
          + 'display:flex;align-items:center;justify-content:center;height:100vh">'
          + 'Opening the Communication Gateway…</body>');
      } catch (e) { /* cross-origin write guard */ }
    }
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/comm-gateway/session`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not open Communications.');
      const url = `${data.admin_url}#cmsToken=${encodeURIComponent(data.token)}`;
      if (w) w.location.replace(url); else window.open(url, '_blank', 'noopener');
    } catch (e) {
      const msg = e.message || 'Could not open Communications.';
      if (w) {
        try { w.document.body.innerHTML = `<div style="font:15px system-ui;color:#b45309;padding:40px">${msg}</div>`; }
        catch (_) { try { w.close(); } catch (__) { /* ignore */ } }
      }
    }
  };

  const handleSubMenuClick = (title) => {
    setOpenSubMenu((prev) => ({ ...prev, [title]: !prev[title] }));
  };

  const isPathActive = (path) => location.pathname === path;

  const isSubPathActive = (subItems) =>
    subItems?.some((item) => location.pathname === item.path || isSubPathActive(item.subItems));

  // Role/permission-based visibility. A leaf is visible when the user holds its
  // requiredPerm (and passes any superAdminOnly gate); a group is visible only
  // when at least one of its children is visible — so empty groups disappear.
  // A dual-access user acting in Business mode should behave like an account
  // admin (so hideForSuperAdmin items like the Dashboard reappear); in Platform
  // mode they behave like a platform admin.
  const inBusinessMode = panelMode === 'business';
  // Hide leaves that belong to the OTHER panel once a mode is chosen.
  const outOfPanel = (item) => panelMode && !item.subItems && panelOf(item) !== 'both' && panelOf(item) !== panelMode;

  const subItemAllowed = (item) => {
    if (outOfPanel(item)) return false;
    if (item.hideForSuperAdmin && isPlatformAdmin && !inBusinessMode) return false;
    if (item.superAdminOnly && !isSuperAdmin) return false;
    if (item.requiredIntegration && !hasIntegration(item.requiredIntegration)) return false;
    if (item.requiredPerm && !hasPermission(item.requiredPerm)) return false;
    if (item.platformPerm && !hasPlatformPermission(item.platformPerm)) return false;
    if (item.subItems) return item.subItems.some(subItemAllowed);
    return true;
  };
  const itemAllowed = (item) => {
    if (outOfPanel(item)) return false;
    // Some items are account/org-scoped and must not appear for the platform-only
    // super admin (e.g. the consent-metrics Dashboard — Admin Panel is its home).
    if (item.hideForSuperAdmin && isPlatformAdmin && !inBusinessMode) return false;
    if (item.superAdminOnly && !isSuperAdmin) return false;
    // External tools (CISO / OpenMetadata) appear only when that integration is
    // enabled for the active org.
    if (item.requiredIntegration && !hasIntegration(item.requiredIntegration)) return false;
    if (item.requiredPerm && !hasPermission(item.requiredPerm)) return false;
    if (item.platformPerm && !hasPlatformPermission(item.platformPerm)) return false;
    if (item.subItems) return item.subItems.some(subItemAllowed);
    return true;
  };

  // Auto-expand submenu if any of its items are active
  useEffect(() => {
    menuItems.forEach((item) => {
      if (item.subItems && isSubPathActive(item.subItems)) {
        setOpenSubMenu((prev) => ({ ...prev, [item.title]: true }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleProfileMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleProfileMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    handleProfileMenuClose();
    logout();
    navigate('/login');
  };

  // Auto-expand sub-menus if the current route matches any sub-item (up to 3 levels)
  useEffect(() => {
    const newOpenSubMenu = {};
    menuItems.forEach((item) => {
      if (!item.subItems) return;
      item.subItems.forEach((sub) => {
        if (location.pathname === sub.path) {
          newOpenSubMenu[item.title] = true;
        }
        if (sub.subItems) {
          const deepMatch = sub.subItems.some((deep) => location.pathname === deep.path);
          if (deepMatch) {
            newOpenSubMenu[item.title] = true;
            newOpenSubMenu[`${item.title}/${sub.title}`] = true;
          }
        }
      });
    });
    setOpenSubMenu((prev) => ({ ...prev, ...newOpenSubMenu }));
  }, [location.pathname]);

  useEffect(() => {
    async function fetchUserInfo() {
      if (user && user.token) {
        setUserLoading(true);
        try {
          const res = await fetch(`${API_BASE_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${user.token}` },
          });
          if (res.ok) {
            const data = await res.json();
            setUserInfo(data.user || data);
          } else {
            setUserInfo(null);
          }
        } catch (e) {
          setUserInfo(null);
        } finally {
          setUserLoading(false);
        }
      } else {
        setUserInfo(null);
      }
    }
    fetchUserInfo();
  }, [user]);

  const displayName =
    userInfo?.first_name || userInfo?.last_name
      ? `${userInfo?.first_name || ''} ${userInfo?.last_name || ''}`.trim()
      : userInfo?.email || '';
  const avatarLetter = userInfo?.first_name
    ? userInfo.first_name.charAt(0).toUpperCase()
    : userInfo?.email
    ? userInfo.email.charAt(0).toUpperCase()
    : '';

  // Shared active-state style for a top-level nav button (no layout-shifting
  // transforms — a soft pill + a left accent rail drawn via ::before).
  const navButtonSx = (active) => ({
    borderRadius: '10px',
    mb: 0.25,
    py: 1,
    px: 1.5,
    minHeight: 42,
    position: 'relative',
    transition: 'background-color .18s ease, color .18s ease',
    color: active ? SHELL.accentDk : SHELL.navText,
    bgcolor: active ? SHELL.accentSoft : 'transparent',
    '&.Mui-selected': { bgcolor: SHELL.accentSoft },
    '&.Mui-selected:hover': { bgcolor: SHELL.accentSoft },
    '&:hover': { bgcolor: active ? SHELL.accentSoft : SHELL.hover },
    '&:focus-visible': { outline: `2px solid ${SHELL.accent}`, outlineOffset: '-2px' },
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
    ...(active && {
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 4,
        top: 9,
        bottom: 9,
        width: 3,
        borderRadius: 3,
        bgcolor: SHELL.accent,
      },
    }),
  });

  const drawer = (
    <Box sx={{ height: '100%', bgcolor: SHELL.surface, display: 'flex', flexDirection: 'column', borderRight: `1px solid ${SHELL.border}` }}>
      {/* Brand */}
      <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${SHELL.border}`, display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #FF6600 0%, #FF8A1E 100%)',
            display: 'grid',
            placeItems: 'center',
            boxShadow: '0 4px 12px rgba(255,102,0,0.3)',
            flexShrink: 0,
          }}
        >
          <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1.05rem', fontFamily: FONT }}>{BRANDING.brandInitial}</Typography>
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: SHELL.text, fontFamily: FONT, fontWeight: 800, fontSize: '0.98rem', lineHeight: 1.1, letterSpacing: '-0.02em' }}>
            {BRANDING.brandName}
          </Typography>
          <Typography sx={{ color: SHELL.textDim, fontFamily: FONT, fontWeight: 500, fontSize: '0.7rem', letterSpacing: '0.02em', lineHeight: 1.3 }}>
            {BRANDING.productName}
          </Typography>
        </Box>
      </Box>

      {/* Navigation */}
      <List sx={{ flex: 1, overflowY: 'auto', px: 1.25, py: 1 }}>
        {menuItems
          .filter(itemAllowed)
          .map((item) => {
          const active = isPathActive(item.path) || isSubPathActive(item.subItems);
          return (
            <React.Fragment key={item.title}>
              <ListItem disablePadding>
                <ListItemButton
                  disableRipple={false}
                  onClick={() => {
                    if (item.subItems) {
                      handleSubMenuClick(item.title);
                    } else if (item.externalUrl) {
                      window.open(item.externalUrl, '_blank', 'noopener,noreferrer');
                    } else {
                      navigate(item.path);
                      if (isMobile) setMobileOpen(false);
                    }
                  }}
                  selected={active}
                  sx={navButtonSx(active)}
                >
                  <ListItemIcon sx={{ color: active ? SHELL.accent : SHELL.textDim, minWidth: 34, '& .MuiSvgIcon-root': { fontSize: '1.2rem' } }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.title}
                    primaryTypographyProps={{
                      fontFamily: FONT,
                      fontWeight: active ? 700 : 500,
                      fontSize: '0.85rem',
                      color: active ? SHELL.accentDk : SHELL.navText,
                      letterSpacing: '-0.01em',
                    }}
                  />
                  {item.subItems && (
                    <Box sx={{ color: active ? SHELL.accent : SHELL.textDim, display: 'flex' }}>
                      {openSubMenu[item.title] ? <ExpandLess sx={{ fontSize: '1.1rem' }} /> : <ExpandMore sx={{ fontSize: '1.1rem' }} />}
                    </Box>
                  )}
                </ListItemButton>
              </ListItem>

              {item.subItems && (
                <Collapse in={openSubMenu[item.title]} timeout={260} unmountOnExit>
                  <List
                    component="div"
                    disablePadding
                    sx={{
                      ml: 2.25,
                      mr: 0.5,
                      my: 0.25,
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 6,
                        top: 4,
                        bottom: 4,
                        width: '1.5px',
                        bgcolor: SHELL.border,
                        borderRadius: '1px',
                      },
                    }}
                  >
                    {item.subItems
                      .filter(subItemAllowed)
                      .map((subItem) => {
                        const subKey = `${item.title}/${subItem.title}`;
                        const subActive = isPathActive(subItem.path) || isSubPathActive(subItem.subItems);
                        return (
                          <React.Fragment key={subItem.title}>
                            <ListItemButton
                              onClick={() => {
                                if (subItem.subItems) {
                                  handleSubMenuClick(subKey);
                                } else if (subItem.launch === 'commGateway') {
                                  openCommGateway();
                                  if (isMobile) setMobileOpen(false);
                                } else {
                                  navigate(subItem.path);
                                  if (isMobile) setMobileOpen(false);
                                }
                              }}
                              selected={subActive}
                              sx={{
                                borderRadius: '8px',
                                py: 0.65,
                                px: 1.25,
                                mb: 0.2,
                                minHeight: 36,
                                ml: 1.25,
                                transition: 'background-color .15s ease, color .15s ease',
                                color: subActive ? SHELL.accentDk : SHELL.navText,
                                bgcolor: subActive ? SHELL.accentSoft : 'transparent',
                                '&.Mui-selected': { bgcolor: SHELL.accentSoft },
                                '&.Mui-selected:hover': { bgcolor: SHELL.accentSoft },
                                '&:hover': { bgcolor: subActive ? SHELL.accentSoft : SHELL.hover },
                                '&:focus-visible': { outline: `2px solid ${SHELL.accent}`, outlineOffset: '-2px' },
                                '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                              }}
                            >
                              {subItem.icon && (
                                <ListItemIcon sx={{ color: subActive ? SHELL.accent : SHELL.textDim, minWidth: 28, '& .MuiSvgIcon-root': { fontSize: '1rem' } }}>
                                  {subItem.icon}
                                </ListItemIcon>
                              )}
                              <ListItemText
                                primary={subItem.title}
                                primaryTypographyProps={{
                                  fontFamily: FONT,
                                  fontWeight: subActive ? 600 : 500,
                                  fontSize: '0.8rem',
                                  color: subActive ? SHELL.accentDk : SHELL.navText,
                                  letterSpacing: '-0.005em',
                                }}
                              />
                              {subItem.subItems &&
                                (openSubMenu[subKey] ? (
                                  <ExpandLess sx={{ fontSize: '1rem', color: subActive ? SHELL.accent : SHELL.textDim }} />
                                ) : (
                                  <ExpandMore sx={{ fontSize: '1rem', color: subActive ? SHELL.accent : SHELL.textDim }} />
                                ))}
                            </ListItemButton>

                            {/* 3rd level */}
                            {subItem.subItems && (
                              <Collapse in={openSubMenu[subKey]} timeout={200} unmountOnExit>
                                <List component="div" disablePadding sx={{ ml: 2 }}>
                                  {subItem.subItems.map((deepItem) => {
                                    const deepActive = isPathActive(deepItem.path);
                                    return (
                                      <ListItemButton
                                        key={deepItem.title}
                                        onClick={() => {
                                          navigate(deepItem.path);
                                          if (isMobile) setMobileOpen(false);
                                        }}
                                        selected={deepActive}
                                        sx={{
                                          borderRadius: '6px',
                                          py: 0.5,
                                          px: 1.25,
                                          mb: 0.15,
                                          minHeight: 30,
                                          transition: 'background-color .15s ease',
                                          color: deepActive ? SHELL.accentDk : SHELL.textDim,
                                          bgcolor: deepActive ? SHELL.accentSoft : 'transparent',
                                          '&.Mui-selected': { bgcolor: SHELL.accentSoft },
                                          '&.Mui-selected:hover': { bgcolor: SHELL.accentSoft },
                                          '&:hover': { bgcolor: deepActive ? SHELL.accentSoft : SHELL.hover },
                                          '&:focus-visible': { outline: `2px solid ${SHELL.accent}`, outlineOffset: '-2px' },
                                        }}
                                      >
                                        <ListItemText
                                          primary={deepItem.title}
                                          primaryTypographyProps={{
                                            fontFamily: FONT,
                                            fontWeight: deepActive ? 600 : 400,
                                            fontSize: '0.75rem',
                                            color: deepActive ? SHELL.accentDk : SHELL.textDim,
                                          }}
                                        />
                                      </ListItemButton>
                                    );
                                  })}
                                </List>
                              </Collapse>
                            )}
                          </React.Fragment>
                        );
                      })}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
      </List>

      {/* Footer */}
      {BRANDING.poweredBy ? (
        <Box sx={{ flexShrink: 0, px: 2.5, py: 1.5, borderTop: `1px solid ${SHELL.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
          <Box sx={{ width: 18, height: 18, borderRadius: '5px', background: 'linear-gradient(135deg, #FF6600 0%, #FF8A1E 100%)', display: 'grid', placeItems: 'center' }}>
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.6rem', fontFamily: FONT }}>{BRANDING.brandInitial}</Typography>
          </Box>
          <Typography sx={{ fontFamily: FONT, color: SHELL.textDim, fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.01em' }}>
            Powered by {BRANDING.poweredBy}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: SHELL.surface,
          color: SHELL.text,
          boxShadow: 'none',
          borderBottom: `1px solid ${SHELL.border}`,
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 60, sm: 64 } }}>
          <IconButton
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 1.5, color: SHELL.navText, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          {currentSection ? (
            <Typography
              component="div"
              aria-label="Current section"
              sx={{
                flexGrow: 1,
                fontFamily: FONT,
                fontWeight: 700,
                fontSize: { xs: '0.72rem', sm: '0.78rem' },
                color: SHELL.textDim,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
              noWrap
            >
              {currentSection}
            </Typography>
          ) : (
            <Box sx={{ flexGrow: 1 }} />
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 2 }}>
            {/* Organization switching is a business/tenant action — hide it in the
                platform panel so the two workspaces stay separate. */}
            {panelMode !== 'platform' && <OrgSwitcher />}
            <Tooltip title={displayName ? `${displayName}${userInfo?.email ? ` · ${userInfo.email}` : ''}` : 'Account'} arrow>
            <ButtonBase
              onClick={handleProfileMenuOpen}
              aria-label={displayName ? `Account: ${displayName}` : 'account of current user'}
              aria-controls="profile-menu"
              aria-haspopup="true"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 0.5,
                px: { xs: 0.5, sm: 1 },
                borderRadius: '999px',
                transition: 'background-color .15s ease',
                '&:hover': { bgcolor: SHELL.hover },
                '&:focus-visible': { outline: `2px solid ${SHELL.accent}`, outlineOffset: 2 },
              }}
            >
              <Avatar sx={{ width: 32, height: 32, fontSize: '0.85rem', fontWeight: 700, bgcolor: SHELL.accent }}>
                {avatarLetter}
              </Avatar>
              <Typography
                sx={{
                  display: { xs: 'none', sm: 'block' },
                  fontFamily: FONT,
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: SHELL.text,
                  maxWidth: 160,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {userLoading ? 'Loading…' : displayName}
              </Typography>
              <KeyboardArrowDownIcon sx={{ fontSize: 18, color: SHELL.textDim, display: { xs: 'none', sm: 'block' } }} />
            </ButtonBase>
            </Tooltip>
          </Box>
          <Menu
            id="profile-menu"
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            keepMounted
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            PaperProps={{
              elevation: 0,
              sx: { mt: 1, minWidth: 220, borderRadius: '12px', border: `1px solid ${SHELL.border}`, boxShadow: '0 8px 28px rgba(15,23,42,0.12)' },
            }}
          >
            <Box sx={{ px: 2, py: 1.25 }}>
              <Typography sx={{ fontFamily: FONT, fontWeight: 600, fontSize: '0.88rem', color: SHELL.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {displayName || 'Account'}
              </Typography>
              {userInfo?.email && (
                <Typography sx={{ fontFamily: FONT, fontSize: '0.76rem', color: SHELL.textDim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {userInfo.email}
                </Typography>
              )}
            </Box>
            <Divider sx={{ borderColor: SHELL.border }} />
            <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/profile'); }} sx={{ gap: 1.25, color: SHELL.text, fontFamily: FONT, fontSize: '0.85rem', fontWeight: 500, py: 1.1 }}>
              <ProfileIcon sx={{ fontSize: 18, color: SHELL.textDim }} />
              My Profile
            </MenuItem>
            <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/account-settings'); }} sx={{ gap: 1.25, color: SHELL.text, fontFamily: FONT, fontSize: '0.85rem', fontWeight: 500, py: 1.1 }}>
              <AccountSettingsIcon sx={{ fontSize: 18, color: SHELL.textDim }} />
              Account Settings
            </MenuItem>
            {isDualAccess && (
              <MenuItem onClick={handleSwitchPanel} sx={{ gap: 1.25, color: SHELL.text, fontFamily: FONT, fontSize: '0.85rem', fontWeight: 500, py: 1.1 }}>
                <SwitchAccountIcon sx={{ fontSize: 18, color: SHELL.textDim }} />
                Switch panel
              </MenuItem>
            )}
            {(roles?.length || 0) > 1 && (
              <MenuItem onClick={handleSwitchRole} sx={{ gap: 1.25, color: SHELL.text, fontFamily: FONT, fontSize: '0.85rem', fontWeight: 500, py: 1.1 }}>
                <SwitchAccountIcon sx={{ fontSize: 18, color: SHELL.textDim }} />
                Switch role
              </MenuItem>
            )}
            <Divider sx={{ borderColor: SHELL.border }} />
            <MenuItem onClick={handleLogout} sx={{ gap: 1.25, color: SHELL.danger, fontFamily: FONT, fontSize: '0.85rem', fontWeight: 600, py: 1.1 }}>
              <LogoutIcon sx={{ fontSize: 18 }} />
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, border: 'none', boxShadow: '8px 0 28px rgba(15,23,42,0.12)' },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, border: 'none' },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: 'background.default',
          minHeight: '100vh',
          overflow: 'auto',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 60, sm: 64 } }} />
        {isImpersonating && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
              px: 2,
              py: 1,
              mb: 2,
              borderRadius: '10px',
              bgcolor: 'rgba(180,83,9,0.10)',
              border: '1px solid rgba(180,83,9,0.30)',
            }}
          >
            <VisibilityIcon sx={{ fontSize: 18, color: '#B45309' }} />
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#92400E', flex: 1, minWidth: 0 }}>
              Impersonating{displayName ? ` ${displayName}` : ' a tenant user'} — actions are performed as this user and are audit-logged.
            </Typography>
            <Button
              size="small"
              variant="contained"
              onClick={exitImpersonation}
              sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.78rem', bgcolor: '#B45309', '&:hover': { bgcolor: '#92400E' } }}
            >
              Exit impersonation
            </Button>
          </Box>
        )}
        <AnnouncementBanner />
        {children}
      </Box>
    </Box>
  );
}

export default Layout;
